<script setup lang="ts">
import { ref, computed } from 'vue'
import { Users, Shield, FileText, Megaphone } from 'lucide-vue-next'
import type { WorkScenario } from '@/types/landing'

interface Props {
  scenarios: WorkScenario[]
}

interface ParsedField {
  title: string
  description: string
}

const props = defineProps<Props>()
const activeIndex = ref(0)

const iconMap: Record<string, any> = {
  Users,
  Shield,
  FileText,
  Megaphone,
}

/**
 * 解析 painPoint / solution 字段，兼容两种格式：
 * - JSON 字符串：{"title":"传统痛点","description":"走访记录靠纸质表格..."} → 提取 title+description
 * - 纯文本字符串：直接作为 description 返回，title 使用 fallback
 */
function parseScenarioField(raw: string, fallbackTitle: string): ParsedField {
  if (!raw) return { title: fallbackTitle, description: '' }
  const trimmed = raw.trim()
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (parsed && typeof parsed === 'object') {
        return {
          title: parsed.title || fallbackTitle,
          description: parsed.description || trimmed,
        }
      }
    } catch {
      // JSON 解析失败，按纯文本处理
    }
  }
  return { title: fallbackTitle, description: trimmed }
}

function setActive(index: number) {
  activeIndex.value = index
}

function goPrev() {
  activeIndex.value = (activeIndex.value - 1 + props.scenarios.length) % props.scenarios.length
}

function goNext() {
  activeIndex.value = (activeIndex.value + 1) % props.scenarios.length
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowLeft') {
    goPrev()
  } else if (event.key === 'ArrowRight') {
    goNext()
  }
}

// 移动端触摸滑动
const touchStartX = ref(0)
function handleTouchStart(event: TouchEvent) {
  touchStartX.value = event.touches[0].clientX
}
function handleTouchEnd(event: TouchEvent) {
  const diff = event.changedTouches[0].clientX - touchStartX.value
  if (Math.abs(diff) > 50) {
    if (diff > 0) goPrev()
    else goNext()
  }
}

const activeScenario = computed(() => props.scenarios[activeIndex.value])

/** 当前激活场景的痛点解析结果 */
const activePainPoint = computed<ParsedField>(() =>
  activeScenario.value
    ? parseScenarioField(activeScenario.value.painPoint, '传统痛点')
    : { title: '传统痛点', description: '' },
)

/** 当前激活场景的解法解析结果 */
const activeSolution = computed<ParsedField>(() =>
  activeScenario.value
    ? parseScenarioField(activeScenario.value.solution, '智台账解法')
    : { title: '智台账解法', description: '' },
)

// 移动端监听
const isMobile = ref(false)
if (typeof window !== 'undefined') {
  const mql = window.matchMedia('(max-width: 767px)')
  isMobile.value = mql.matches
  mql.addEventListener('change', (e) => { isMobile.value = e.matches })
}
</script>

<template>
  <section
    id="scenarios"
    class="bg-white py-20 md:py-24"
    tabindex="0"
    @keydown="handleKeydown"
  >
    <div class="mx-auto max-w-[1200px] px-6">
      <!-- 标题 -->
      <div class="mb-10 text-center">
        <h2 class="mb-3 text-2xl font-semibold text-[#1D2129] md:text-3xl">
          覆盖基层高频工作场景
        </h2>
        <p class="mx-auto max-w-[560px] text-base text-[#86909C]">
          传统痛点 vs 智台账解法，看看哪个是你的日常
        </p>
      </div>

      <!-- Tab 切换 -->
      <div role="tablist" class="mb-8 flex justify-center gap-1 rounded-xl bg-[#F2F3F5] p-1">
        <button
          v-for="(scenario, index) in scenarios"
          :key="scenario.id"
          role="tab"
          :aria-selected="index === activeIndex"
          :class="[
            'relative flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-300',
            index === activeIndex
              ? 'bg-white text-indigo-600 shadow-sm'
              : 'text-[#86909C] hover:text-[#4E5969]',
          ]"
          @click="setActive(index)"
        >
          <component :is="iconMap[scenario.icon as keyof typeof iconMap]" class="h-4 w-4" />
          <span>{{ scenario.title }}</span>
        </button>
      </div>

      <!-- 场景内容 -->
      <div
        class="transition-all duration-400"
        @touchstart="handleTouchStart"
        @touchend="handleTouchEnd"
      >
        <div
          v-if="isMobile"
          class="flex flex-col gap-4"
        >
          <!-- 移动端：上下堆叠 -->
          <div
            class="rounded-xl bg-gray-50 p-6 transition-all duration-400"
            :key="activeScenario.id + '-pain'"
          >
            <div class="mb-2 flex items-center gap-2">
              <span class="rounded bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-600">{{ activePainPoint.title }}</span>
            </div>
            <p class="text-sm leading-relaxed text-[#4E5969]">{{ activePainPoint.description }}</p>
          </div>
          <div
            class="rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white transition-all duration-400"
            :key="activeScenario.id + '-sol'"
          >
            <div class="mb-2 flex items-center gap-2">
              <span class="rounded bg-white/20 px-2 py-0.5 text-xs font-semibold">{{ activeSolution.title }}</span>
            </div>
            <p class="text-sm leading-relaxed text-white/90">{{ activeSolution.description }}</p>
          </div>
        </div>

        <div
          v-else
          class="flex items-center gap-6"
        >
          <!-- 桌面端：左右分栏 -->
          <div class="flex-1 rounded-xl bg-gray-50 p-8">
            <div class="mb-3 inline-block rounded bg-gray-200 px-3 py-1 text-xs font-semibold text-gray-600">
              {{ activePainPoint.title }}
            </div>
            <p class="text-base leading-relaxed text-[#4E5969]">{{ activePainPoint.description }}</p>
          </div>

          <!-- 连接箭头 -->
          <div class="flex-shrink-0 text-indigo-500">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M5 12h14" />
              <path d="M12 5l7 7-7 7" />
            </svg>
          </div>

          <div class="flex-1 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 p-8 text-white">
            <div class="mb-3 inline-block rounded bg-white/20 px-3 py-1 text-xs font-semibold">
              {{ activeSolution.title }}
            </div>
            <p class="text-base leading-relaxed text-white/90">{{ activeSolution.description }}</p>
          </div>
        </div>

        <!-- 指示点 -->
        <div class="mt-8 flex items-center justify-center gap-2">
          <button
            v-for="(_, index) in scenarios"
            :key="index"
            class="h-2 rounded-full transition-all duration-200"
            :class="index === activeIndex ? 'w-6 bg-indigo-600' : 'w-2 bg-[#C9CDD4] hover:bg-[#86909C]'"
            :aria-label="`切换到${scenarios[index].title}`"
            @click="setActive(index)"
          />
        </div>
      </div>
    </div>
  </section>
</template>
